import { gql } from "@apollo/client";
import { BigNumber } from "ethers/lib/ethers";

import { Stat, StatGrid } from "@/components/design-system";
import { cryptoToFloat, formatPercent } from "@/lib/format";
import { ProtocolMetricsFieldsFragment } from "@/lib/graphql/generated";

export const PROTOCOL_METRICS_FIELDS = gql`
  fragment ProtocolMetricsFields on Protocol {
    id
    totalDrawdowns
    totalWritedowns
    defaultRate
    totalReserveCollected
    totalInterestCollected
    totalPrincipalCollected
  }
`;

interface GoldfinchPoolsMetricsProps {
  className?: string;
  protocol: ProtocolMetricsFieldsFragment;
}

const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  notation: "compact",
  style: "currency",
  currency: "USD",
});
// Expresses metric abbreviated in millions "M"
const formatForMetrics = (amount: BigNumber) => {
  const float = cryptoToFloat({ amount, token: "USDC" });
  return numberFormatter.format(float);
};

// This amount was not paid directly through the TranchedPool. It instead was paid to backers through a side channel, and must be considered an amount repaid to the protocol
const warblerStratosBackstop = BigNumber.from(1_653_340_601_634);
const lendeastBackstop = BigNumber.from(331_143_220_000);

// This amount was forfeited by Warbler Labs and Stratos. It should be deducted from both active loans and loans repaid
const warblerStratosForfeiture = BigNumber.from(2_412_054_665_764);

export function GoldfinchPoolsMetrics({
  className,
  protocol,
}: GoldfinchPoolsMetricsProps) {
  const {
    totalDrawdowns,
    totalPrincipalCollected,
    totalWritedowns,
    totalInterestCollected,
    totalReserveCollected,
    defaultRate,
  } = protocol;

  const activeLoans = totalDrawdowns
    .sub(totalPrincipalCollected)
    .sub(totalWritedowns)
    .sub(warblerStratosBackstop)
    .sub(warblerStratosForfeiture);

  const totalLoansRepaid = totalPrincipalCollected
    .add(totalInterestCollected)
    .sub(totalReserveCollected)
    .add(warblerStratosBackstop)
    .add(lendeastBackstop);

  return (
    <StatGrid
      bgColor="mustard-50"
      borderColor="sand-300"
      className={className}
      size="lg"
    >
      <Stat
        label="Active Loans"
        tooltip="Total principal outstanding across all loans in the Goldfinch Protocol."
        value={formatForMetrics(activeLoans)}
      />
      <Stat
        label="Total Loss Rate"
        tooltip="Total value of loans written down or written off (losses) as a proportion of total loans issued on the Goldfinch protocol."
        value={formatPercent(defaultRate)}
      />
      <Stat
        label="Total Loans Repaid"
        tooltip="Total amount of principal and interest repaid across all loans in the Goldfinch Protocol."
        value={formatForMetrics(totalLoansRepaid)}
      />
    </StatGrid>
  );
}

export function GoldfinchPoolsMetricsPlaceholder({
  className,
}: {
  className?: string;
}) {
  return (
    <StatGrid
      bgColor="mustard-50"
      borderColor="sand-300"
      className={className}
      size="lg"
    >
      <Stat label="Active Loans" />
      <Stat label="Total Loss Rate" />
      <Stat label="Total Loans Repaid" />
    </StatGrid>
  );
}
